import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { GameNotification } from '../types';

interface NotificationSystemProps {
  notifications: GameNotification[];
  removeNotification: (id: string) => void;
}

// ULTRA SIMPLE NOTIFICATION ITEM - NO STATE, DIRECT REMOVAL
const NotificationItem: React.FC<{
  notification: GameNotification;
  onRemove: (id: string) => void;
}> = ({ notification, onRemove }) => {
  
  // Auto-remove after the notification's own duration (fallback to 2000ms)
  useEffect(() => {
    const autoDuration = Math.max(500, notification.duration ?? 2000);
    console.log(`📝 Auto-removing notification ${notification.id} in ${autoDuration}ms`);
    const timer = setTimeout(() => {
      console.log(`⏰ Auto-removing notification ${notification.id}`);
      onRemove(notification.id);
    }, autoDuration);
    
    return () => clearTimeout(timer);
  }, [notification.id, notification.duration, onRemove]);
  
  // Get the appropriate background color based on notification type
  const getBackgroundColor = (type: string): string => {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
      default:
        return 'bg-blue-500';
    }
  };
  
  // Manually remove notification - DIRECT REMOVAL
  const handleRemoveClick = () => {
    console.log(`🖱️ Manual close clicked for notification ${notification.id}`);
    onRemove(notification.id);
  };
  
  return (
    <div
      className={`${getBackgroundColor(notification.type)} text-white p-3 rounded-lg shadow-lg 
                  flex justify-between items-start max-w-xs transition-all duration-300 opacity-100`}
    >
      <div className="mr-2">{notification.message}</div>
      <button 
        onClick={handleRemoveClick}
        className="text-white hover:text-gray-200 flex-shrink-0"
      >
        <X size={18} />
      </button>
    </div>
  );
};

const NotificationSystem: React.FC<NotificationSystemProps> = ({
  notifications,
  removeNotification
}) => {
  console.log('🔔 NotificationSystem render - notifications count:', notifications.length);
  
  // FORCE CLEANUP: Remove any notification older than its specified duration (or 5s by default)
  useEffect(() => {
    const now = Date.now();
    notifications.forEach(notification => {
      const age = now - notification.timestamp;
      const maxAge = Math.max(1000, notification.duration ?? 5000);
      if (age > maxAge) {
        console.log(`🧹 Force removing old notification ${notification.id} (age: ${age}ms > ${maxAge}ms)`);
        removeNotification(notification.id);
      }
    });
  }, [notifications, removeNotification]);
  
  // Show only the 3 most recent notifications
  const visibleNotifications = notifications.slice(-3);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-h-[80vh] overflow-hidden">
      {visibleNotifications.map((notification) => (
        <NotificationItem 
          key={notification.id} 
          notification={notification} 
          onRemove={removeNotification} 
        />
      ))}
    </div>
  );
};

export default NotificationSystem;
